import type { ReactNode } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, Check, Loader2 } from "lucide-react"

/**
 * Feuille d'édition générique — une seule mécanique de modale pour les trois écrans du
 * Profil. Elle n'impose pas le contenu du champ : chaque appelant compose son propre
 * corps (texte, liste de choix, sélection multiple), ce qui évite un composant à quinze
 * variantes pour six formulaires différents.
 */
export function EditSheet({
  open, title, description, children, onSave, onCancel, saving, saveLabel = "Enregistrer", danger,
}: {
  open: boolean
  title: string
  description?: ReactNode
  children: ReactNode
  onSave?: () => void
  onCancel: () => void
  saving?: boolean
  saveLabel?: string
  danger?: boolean
}) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-[4px]"
            onClick={() => !saving && onCancel()}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed z-[100] left-4 right-4 top-1/2 -translate-y-1/2 max-w-[420px] mx-auto p-6 rounded-[22px] max-h-[86vh] overflow-y-auto"
            style={{ background: "linear-gradient(180deg,#0f1829,#0a1020)", border: "1px solid rgba(30,41,59,0.55)" }}
          >
            <div className="flex items-start justify-between mb-4 gap-3">
              <div className="min-w-0">
                <h3 className="text-[16px] font-bold text-white">{title}</h3>
                {description && (
                  <p className="text-[12px] text-gray-500 mt-1 leading-relaxed">{description}</p>
                )}
              </div>
              <button
                onClick={() => !saving && onCancel()}
                aria-label="Fermer"
                // 44 px de cible tactile ; la marge négative garde la croix à sa place.
                className="w-11 h-11 -mt-1.5 -mr-1.5 rounded-lg grid place-items-center cursor-pointer hover:bg-white/[0.04] shrink-0"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {children}

            {onSave && (
              <div className="grid grid-cols-2 gap-3 mt-5">
                <button
                  onClick={onCancel}
                  disabled={saving}
                  className="py-3 rounded-xl text-[14px] font-semibold text-gray-400 cursor-pointer disabled:opacity-50"
                  style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}
                >
                  Annuler
                </button>
                <button
                  onClick={onSave}
                  disabled={saving}
                  className="py-3 rounded-xl text-[14px] font-bold text-white cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2"
                  style={{
                    background: danger
                      ? "linear-gradient(135deg,#dc2626,#ef4444)"
                      : "linear-gradient(135deg,#0369a1,#06b6d4)",
                  }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {saveLabel}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

/** Champ texte / nombre / date, mis en forme comme le reste des feuilles d'édition. */
export function SheetInput({
  value, onChange, type = "text", unit, placeholder, step, autoFocus = true,
}: {
  value: string
  onChange: (v: string) => void
  type?: "text" | "number" | "date" | "tel" | "password"
  unit?: string
  placeholder?: string
  step?: string
  autoFocus?: boolean
}) {
  return (
    <div
      className="flex items-center gap-3 px-4 h-[48px] rounded-xl"
      style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)" }}
    >
      <input
        type={type}
        value={value}
        step={step}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-gray-600 min-w-0"
        style={{ caretColor: "#06b6d4" }}
      />
      {unit && <span className="text-[13px] text-gray-500 shrink-0">{unit}</span>}
    </div>
  )
}

/** Liste de choix exclusifs. */
export function SheetChoices<T extends string>({
  options, value, onChange,
}: {
  options: { id: T; label: string; hint?: string }[]
  value: T | null
  onChange: (v: T) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      {options.map((o) => {
        const active = o.id === value
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-left cursor-pointer transition-all active:scale-[0.99]"
            style={{
              background: active ? "rgba(6,182,212,0.10)" : "rgba(255,255,255,0.02)",
              border: `1px solid ${active ? "rgba(6,182,212,0.35)" : "rgba(30,41,59,0.55)"}`,
            }}
          >
            <span className="flex-1 min-w-0">
              <span className="block text-[14px] font-medium" style={{ color: active ? "#67e8f9" : "#e2e8f0" }}>{o.label}</span>
              {o.hint && <span className="block text-[11.5px] text-gray-500 mt-0.5">{o.hint}</span>}
            </span>
            {active && <Check className="w-4 h-4 text-cyan-400 shrink-0" />}
          </button>
        )
      })}
    </div>
  )
}
